import { SpiderLeg } from './spiderShapes';

const MOTION_START_DELAY = 0.05;

const DROP_DURATION = 1.3;

const RESTING_LIFTS: Partial<Record<number, RestingLift>> = {
  2: { degrees: 12, duration: 7, begin: MOTION_START_DELAY },
  5: { degrees: 10, duration: 9, begin: 3 },
};

const CLIMBING_GAIT: Gait = {
  reach: 14,
  pull: -8,
  duration: 3.6,
  lead: { steps: 3, start: MOTION_START_DELAY, end: 3.6 },
  trail: { steps: 2, start: 0.6, end: 3 },
};

const SCARED_GAIT: Gait = {
  reach: 12,
  pull: -8,
  duration: 0.9,
  lead: { steps: 4, start: MOTION_START_DELAY, end: 0.9 },
  trail: { steps: 3, start: 0.11, end: 0.785 },
};

export enum SpiderPhase {
  RESTING = 'resting',
  DROPPING = 'dropping',
  DANGLING = 'dangling',
  CLIMBING = 'climbing',
  SCARED = 'scared',
}

interface LegMotion {
  values: number[];
  keyTimes: number[];
  duration: number;
  begin: number;
  isOneShot: boolean;
}

interface RestingLift {
  degrees: number;
  duration: number;
  begin: number;
}

interface GaitGroup {
  steps: number;
  start: number;
  end: number;
}

interface Gait {
  reach: number;
  pull: number;
  duration: number;
  lead: GaitGroup;
  trail: GaitGroup;
}

interface RaiseArgs {
  degrees: number;
  side: number;
}

const raise = ({ degrees, side }: RaiseArgs) => -degrees * side;

interface RestingMotionArgs {
  side: number;
  index: number;
}

const getRestingMotion = ({ side, index }: RestingMotionArgs): LegMotion | null => {
  const lift = RESTING_LIFTS[index];

  if (!lift) {
    return null;
  }

  const { degrees, duration, begin } = lift;
  const raised = raise({ degrees, side });

  return {
    values: [0, 0, raised, raised, 0, 0],
    keyTimes: [0, 0.78, 0.82, 0.88, 0.92, 1],
    duration,
    begin,
    isOneShot: false,
  };
};

interface DroppingMotionArgs {
  tuck: number;
  startAngle: number;
}

const getDroppingMotion = ({ tuck, startAngle }: DroppingMotionArgs): LegMotion => ({
  values: [startAngle, tuck, tuck, -0.3 * tuck, 0],
  keyTimes: [0, 0.12, 0.55, 0.75, 1],
  duration: DROP_DURATION - MOTION_START_DELAY,
  begin: MOTION_START_DELAY,
  isOneShot: true,
});

const getDanglingMotion = (index: number): LegMotion => {
  const drift = (4 + (index % 3)) * (index % 2 === 1 ? 1 : -1);

  return {
    values: [0, drift, 0, -0.6 * drift, 0],
    keyTimes: [0, 0.25, 0.5, 0.75, 1],
    duration: 3.2 + (index % 4) * 0.5,
    begin: MOTION_START_DELAY + index * 0.37,
    isOneShot: false,
  };
};

interface GaitMotionArgs {
  side: number;
  index: number;
  gait: Gait;
  startAngle: number;
}

const getGaitMotion = ({ side, index, gait, startAngle }: GaitMotionArgs): LegMotion => {
  const { reach, pull, duration, lead, trail } = gait;
  const { steps, start, end } = index % 4 === 0 || index % 4 === 3 ? lead : trail;
  const stepLength = (end - start) / steps;
  const span = duration - MOTION_START_DELAY;
  const stepPoints = Array.from({ length: steps }, (_, step) => start + step * stepLength).flatMap(
    (stepStart) => [
      [stepStart + 0.35 * stepLength, raise({ degrees: reach, side })],
      [stepStart + 0.6 * stepLength, raise({ degrees: pull, side })],
      [stepStart + stepLength, 0],
    ]
  );
  const points = [
    [MOTION_START_DELAY, startAngle],
    [start, startAngle],
    ...stepPoints,
    [duration, 0],
  ].filter(([time], position, all) => position === 0 || time > all[position - 1][0] + 0.001);

  return {
    values: points.map(([, angle]) => angle),
    keyTimes: points.map(([time]) => Number(((time - MOTION_START_DELAY) / span).toFixed(4))),
    duration: span,
    begin: MOTION_START_DELAY,
    isOneShot: true,
  };
};

interface GetLegMotionArgs {
  leg: SpiderLeg;
  index: number;
  phase: SpiderPhase;
  startAngle: number;
}

export const getLegMotion = ({
  leg,
  index,
  phase,
  startAngle,
}: GetLegMotionArgs): LegMotion | null => {
  const { side, tuck } = leg;

  switch (phase) {
    case SpiderPhase.RESTING:
      return getRestingMotion({ side, index });
    case SpiderPhase.DROPPING:
      return getDroppingMotion({ tuck, startAngle });
    case SpiderPhase.DANGLING:
      return getDanglingMotion(index);
    case SpiderPhase.CLIMBING:
      return getGaitMotion({ side, index, gait: CLIMBING_GAIT, startAngle });
    case SpiderPhase.SCARED:
      return getGaitMotion({ side, index, gait: SCARED_GAIT, startAngle });
  }
};
